import { Box, ButtonBase } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { fetchBlobFromUrl } from "../../api/person/person-attachment";
import ImageZoomDialog from "../common/image-zoom-dialog";

interface MarkdownDocImageProps {
	/** The authenticated endpoint the markdown's `src` resolved to. */
	src: string;
	alt: string;
}

/**
 * How far ahead of the viewport a picture starts downloading.
 *
 * Far enough that scrolling at a reading pace finds it already there, short
 * enough that opening a long page fetches a handful rather than all of it.
 */
const PREFETCH_MARGIN = "200px";

/**
 * An image inside rendered markdown, fetched through the authenticated fetch.
 *
 * A bare `<img src="/api/v2/…">` cannot work here: the SPA carries its JWT in
 * request headers, not in a cookie, and the browser sends none of them for an
 * image element. So the bytes are fetched like any other API call and handed to
 * the element as an object URL, which is revoked when the image unmounts.
 *
 * Clicking the image opens it full size in a dialog; screenshots in release
 * notes are unreadable at the column width the surrounding prose is set in.
 *
 * Nothing is downloaded until the picture's place nears the viewport. Both
 * pages that render these — the release-notes archive and the ticket page's
 * preview — put every picture on the screen at once, so fetching on mount
 * cost one authenticated request per picture the moment the page opened,
 * whether or not the reader ever scrolled that far.
 */
const MarkdownDocImage: React.FC<MarkdownDocImageProps> = ({ src, alt }) => {
	const [objectUrl, setObjectUrl] = useState<string>("");
	const [isZoomed, setIsZoomed] = useState(false);
	const [hasApproached, setHasApproached] = useState(false);
	const placeholderRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		if (hasApproached) return;
		const placeholder = placeholderRef.current;
		if (placeholder === null) return;
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) {
					setHasApproached(true);
				}
			},
			{ rootMargin: PREFETCH_MARGIN }
		);
		observer.observe(placeholder);
		return () => observer.disconnect();
	}, [hasApproached]);

	useEffect(() => {
		if (!hasApproached) return;
		let cancelled = false;
		let created = "";
		fetchBlobFromUrl(src)
			.then(({ blob }) => {
				if (cancelled) return;
				created = URL.createObjectURL(blob);
				setObjectUrl(created);
			})
			.catch(() => undefined);
		return () => {
			cancelled = true;
			if (created) URL.revokeObjectURL(created);
		};
	}, [src, hasApproached]);

	if (!objectUrl) {
		// What the observer watches, standing where the picture will go. It
		// reserves no height for it: the list carries no image dimensions, so
		// any reserved height would be a guess, and the page already lays out
		// this way today.
		return (
			<Box
				component="span"
				ref={placeholderRef}
				data-component="markdown-doc-image-placeholder"
				sx={{ display: "block", height: "1px" }}
			/>
		);
	}
	return (
		<>
			<ButtonBase
				data-component="markdown-doc-image"
				aria-label="Enlarge picture"
				title="Click to enlarge"
				onClick={() => setIsZoomed(true)}
				sx={{
					display: "block",
					maxWidth: "100%",
					my: 2,
					borderRadius: 1,
					cursor: "zoom-in",
				}}
			>
				<Box
					component="img"
					src={objectUrl}
					alt={alt}
					sx={{
						display: "block",
						maxWidth: "100%",
						height: "auto",
						borderRadius: 1,
						border: 1,
						borderColor: "divider",
					}}
				/>
			</ButtonBase>
			<ImageZoomDialog
				open={isZoomed}
				onClose={() => setIsZoomed(false)}
				src={objectUrl}
				alt={alt}
			/>
		</>
	);
};

export default MarkdownDocImage;
